<template>
  <div>
    <public-announcement :is-fixed="true" />
    <invitation-notifications v-if="currentUser.id" />
    <edu-yearly-notification v-if="currentUser.id" />
    <div class="relative p-4 md:px-10 md:py-6">
      <OnboardingV3BackArrow />
      <page-breadcrumb :category="$i18nPresenter.t('Account')" :text="breadCumbText" />

      <!-- TABS -->
      <div class="profile-tab">
        <aha-tabs>
          <aha-tab-item :title="titleProfile">
            <aha-section>
              <template #header>
                <h5>{{ $i18nPresenter.t('Profile') }}</h5>
              </template>
              <template #body>
                <UploadImageV2
                  v-if="isUploadImageModalShowing"
                  :type="'Update profile > Avatar'"
                  @hideModal="isUploadImageModalShowing = false"
                  @callback="handleUploadImage"
                  :tabs="uploadAvatarTabs"
                />
                <CropImage
                  v-if="isCropImageModalShowing"
                  :image-url="avatarImageUrl"
                  :aspect-ratio="1"
                  @on-cancel="handleCancelCrop"
                  @hideModal="isCropImageModalShowing = false"
                  @handleCropImageFinished="handleCropImageFinished"
                />
                <div class="container-user-profile">
                  <div class="contain-avatar">
                    <div class="avatar-group">
                      <div class="avatar-image" @click="onClickButtonChangeAvatar">
                        <aha-avatar
                          v-if="currentUser.id"
                          class="mr-2"
                          :image="currentUser.avatar"
                          :name="`${currentUser.firstName} ${currentUser.lastName}`"
                          :id="currentUser.id"
                        />
                        <div
                          v-if="isUploadingAvatar"
                          class="loading-show horizontal layout center center-justified"
                        >
                          <aha-spinner />
                        </div>
                      </div>
                    </div>
                    <aha-button
                      class="px-2.5 sm:px-4"
                      variant="base"
                      size="xl"
                      @click="onClickButtonChangeAvatar"
                    >
                      <aha-icon
                        class="-mt-0.5"
                        name="image"
                        height="19"
                        width="19"
                        viewBox="0 0 25 25"
                      />
                      <span class="sm:inline">{{ $i18nPresenter.t('Upload') }}</span>
                    </aha-button>
                    <div class="mt-3 mb-4 text-xs text-red-100" v-if="errorUploadImage">
                      {{ errorUploadImage }}
                    </div>
                  </div>
                  <div class="contain-info">
                    <div class="mb-4">
                      <label class="block mb-2" for="name">{{ $i18nPresenter.t('Name:') }}</label>
                      <aha-input
                        type="text"
                        required
                        :placeholder="placeholderYourName"
                        id="name"
                        v-model="name"
                        @keyup.enter="updateProfile(1)"
                        :maxLength="40"
                      />
                    </div>
                    <div class="mb-4">
                      <label class="block mb-2" for="preferences">
                        {{ $i18nPresenter.t('Where do you plan to use AhaSlides?') }}
                      </label>
                      <aha-select
                        id="profile-preferences"
                        v-model="userPreferences"
                      >
                        <aha-select-option
                          value=""
                          disabled
                          selected
                          hidden
                        >
                          {{ $i18nPresenter.t('Select your preference…') }}
                        </aha-select-option>
                        <aha-select-option
                          v-for="(option, optionIndex) in onboardingOptions"
                          :value="option.useAhaSlidesFor"
                          :key="optionIndex"
                        >
                          {{ $i18nPresenter.t(option.name) }}
                        </aha-select-option>
                      </aha-select>
                    </div>

                    <div
                      class="mb-4 text-red-100"
                      v-if="textError && openProfileTab == 1"
                    >
                      {{ textError }}
                    </div>

                    <aha-button type="submit" @click="updateProfile(1)">
                      <span class="px-1">{{ $i18nPresenter.t('Update profile') }}</span>
                      <aha-spinner
                        variant="white"
                        v-if="isUserUpdating && openProfileTab == 1"
                        class="ml-1"
                      />
                    </aha-button>
                  </div>
                </div>
              </template>
            </aha-section>
            <!-- Change email for Google account -->
            <aha-section v-if="isShowChangeEmailForGoogle">
              <template #header>
                {{ $i18nPresenter.t('Change email') }}
              </template>
              <template #body>
                <table class="relative table-plan table-change-email">
                  <tbody class="w-full">
                    <tr>
                      <td class="table-plan-property">
                        {{ $i18nPresenter.t('Email address:') }}
                      </td>
                      <td
                        class="flex py-2 table-plan-value"
                      >
                        <div class="flex-grow">
                          {{ currentUser.email }}
                        </div>
                        <div class="pl-3 min-w-max">
                          <aha-link
                            type="primary"
                            variant="primary"
                            @click="handleClickEditEmail()"
                          >
                            <aha-icon
                              name="edit"
                              viewBox="0 0 24 24"
                              width="15"
                              height="15"
                              v-if="!showEditEmail"
                              class="mb-1"
                            />
                            <aha-icon
                              name="close"
                              viewBox="0 0 24 24"
                              width="15"
                              height="15"
                              class="mb-0.5"
                              v-else
                            />
                            <span class="pl-1 font-bold">{{ $i18nPresenter.t('Edit') }}</span>
                          </aha-link>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="showEditEmail">
                      <td class="hidden table-plan-property sm:table-cell" />
                      <!-- No password -->
                      <td v-if="!currentUser.hasPassword" colspan="2">
                        <form @submit.prevent="updateProfile(3)" autocomplete="off">
                          <AhaInfoBox class="mt-3 mb-4">
                            {{ $i18nPresenter.t('In order to log in using the new email, you need to create a new password first.') }}
                          </AhaInfoBox>
                          <div class="mb-4">
                            <label for="email__edit" class="block mb-2">{{ $i18nPresenter.t('Email:') }}</label>
                            <aha-input
                              type="email"
                              required
                              :placeholder="placeholderEnterNewEmail"
                              id="email__edit"
                              v-model="emailEdit"
                            />
                          </div>

                          <div class="mb-4">
                            <label class="block mb-2" for="password__new">{{ $i18nPresenter.t('New password:') }}</label>
                            <aha-input-password
                              required
                              :placeholder="placeholderEnterNewPassword"
                              name="new-password"
                              id="password__new"
                              v-model="newPassword"
                            />
                          </div>

                          <div
                            class="mb-2 text-red-100"
                            v-if="textError && openProfileTab == 3"
                          >
                            {{ textError }}
                          </div>

                          <aha-button
                            type="submit"
                            class="mb-5"
                          >
                            <span class="px-1">{{ $i18nPresenter.t('Update email') }}</span>
                            <aha-spinner
                              variant="white"
                              v-if="isUserUpdating && openProfileTab == 3"
                              class="ml-1"
                            />
                          </aha-button>
                        </form>
                      </td>
                      <!-- Has password -->
                      <td v-if="currentUser.hasPassword" colspan="2">
                        <form @submit.prevent="updateProfile(3)" autocomplete="off">
                          <div class="mb-4">
                            <label class="block mb-2">{{ $i18nPresenter.t('Email:') }}</label>
                            <aha-input
                              type="email"
                              required
                              :placeholder="placeholderYourEmailAddress"
                              v-model="emailEdit"
                            />
                          </div>

                          <div class="mb-4">
                            <label class="block mb-2" for="email-password">
                              {{ $i18nPresenter.t('Confirm password:') }}
                            </label>
                            <aha-input-password
                              required
                              value="password"
                              id="email-password"
                              :placeholder="placeholderEnterYourPassword"
                              v-model="currentPassword3"
                            />
                          </div>
                          <div
                            class="mb-2 text-red-100"
                            v-if="textError && openProfileTab == 3"
                          >
                            {{ textError }}
                          </div>

                          <aha-button
                            type="submit"
                            class="mb-5"
                          >
                            <span class="px-1">{{ $i18nPresenter.t('Update email') }}</span>
                            <aha-spinner
                              variant="white"
                              v-if="isUserUpdating && openProfileTab == 3"
                              class="ml-1"
                            />
                          </aha-button>
                        </form>
                      </td>
                    </tr>
                    <tr>
                      <td class="table-plan-property">
                        {{ $i18nPresenter.t('Linked account:') }}
                      </td>
                      <td class="table-plan-value">
                        <aha-icon
                          :name="iconLinked"
                          maintainColor
                          viewBox="0 0 24 24"
                          width="20"
                          height="20"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </template>
            </aha-section>
            <!-- End change email for Google account -->
            <!-- Change email for ahaslides's account -->
            <aha-section v-else-if="isShowChangeEmailForAhaSlides">
              <template #header>
                <h5>{{ $i18nPresenter.t('Change email') }}</h5>
              </template>
              <template #body>
                <div class="mb-4">
                  <label class="block mb-2">{{ $i18nPresenter.t('Email:') }}</label>
                  <aha-input
                    type="email"
                    required
                    :placeholder="placeholderYourEmailAddress"
                    id="example-email"
                    @keyup.enter="updateProfile(3)"
                    v-model="email"
                  />
                </div>

                <div class="mb-4">
                  <label class="block mb-2" for="email-password">{{ $i18nPresenter.t('Confirm password:') }}</label>
                  <aha-input-password
                    required
                    value="password"
                    id="email-password"
                    :placeholder="placeholderEnterYourPassword"
                    @keyup.enter="updateProfile(3)"
                    v-model="currentPassword3"
                  />
                </div>
                <div
                  class="mb-2 text-red-100"
                  v-if="textError && openProfileTab == 3"
                >
                  {{ textError }}
                </div>

                <aha-button
                  type="submit"
                  @click="updateProfile(3)"
                >
                  <span class="px-1">{{ $i18nPresenter.t('Update email') }}</span>
                  <aha-spinner
                    variant="white"
                    v-if="isUserUpdating && openProfileTab == 3"
                    class="ml-1"
                  />
                </aha-button>
              </template>
            </aha-section>
            <!-- End change email for ahaslides's account -->
          </aha-tab-item>

          <aha-tab-item v-show="isShowChangePassword" :title="titleChangePassword">
            <aha-section>
              <template #header>
                <h5 v-if="!currentUser.hasPassword">
                  {{ $i18nPresenter.t('Create password') }}
                </h5>
                <h5 v-else>
                  {{ $i18nPresenter.t('Change password') }}
                </h5>
              </template>
              <template #body>
                <div class="mb-4" v-if="currentUser.hasPassword">
                  <label class="block mb-2" for="current-password">{{ $i18nPresenter.t('Current password:') }}</label>
                  <aha-input-password
                    required
                    :placeholder="placeholderEnterYourPassword"
                    name="current-password"
                    id="current-password"
                    @keyup.enter="updateProfile(2)"
                    v-model="currentPassword"
                  />
                </div>

                <div class="mb-4">
                  <label class="block mb-2" for="new-password">{{ $i18nPresenter.t('New password:') }}</label>
                  <aha-input-password
                    required
                    :placeholder="placeholderTypeNewPassword"
                    name="new-password"
                    id="new-password"
                    @keyup.enter="updateProfile(2)"
                    v-model="newPassword"
                  />
                </div>
                <div
                  class="mb-2 text-red-100"
                  v-if="textError && openProfileTab == 2"
                >
                  {{ textError }}
                </div>

                <aha-button type="submit" @click="updateProfile(2)">
                  <span class="px-1">{{ $i18nPresenter.t('Update password') }}</span>
                  <aha-spinner
                    variant="white"
                    v-if="isUserUpdating && openProfileTab == 2"
                    class="ml-1"
                  />
                </aha-button>
              </template>
            </aha-section>
          </aha-tab-item>

          <!-- Email preferences -->
          <TabEmailPreferences @hasErrorOnUpdatingUser="hasErrorOnUpdatingUser = true" />

          <TabLogOutEverywhereElse />

          <aha-tab-item :title="titleAccountDeletion">
            <aha-section>
              <template #header>
                <h5>{{ $i18nPresenter.t('Account deletion') }}</h5>
              </template>
              <template #body>
                <div class="pb-2">
                  <div class="pb-2">
                    {{ $i18nPresenter.t('This will permanently delete your account and all presentation data.') }}
                  </div>
                </div>

                <aha-button
                  variant="danger"
                  @click="showDeleteAccountModal('warning')"
                >
                  {{ $i18nPresenter.t('Delete account…') }}
                </aha-button>
              </template>
            </aha-section>
          </aha-tab-item>
        </aha-tabs>
      </div>

      <!-- End of account deletion -->

      <modal-error-my-profile-update-user
        :hasErrorOnUpdatingUser="hasErrorOnUpdatingUser"
        @hide="hasErrorOnUpdatingUser = false"
        @click="hasErrorOnUpdatingUser = false"
      />

      <!-- Delete account modals -->
      <aha-modal
        :title="titleDeleteYourAccount"
        v-if="modalTypeVisible"
        @hide="closeDeleteAccountModal(modalType)"
      >
        <p>
          {{ modalContent.desc }}
        </p>

        <!-- Warning - list content -->
        <ul class="my-2" v-if="modalType === 'warning'">
          <li
            v-for="(content, index) in modalContent.listContent"
            :key="index"
            class="ml-8 list-disc"
            :class="{'hidden': $store.state.plan.name === 'Free' && index === 0}"
          >
            {{ content }}
          </li>
        </ul>
        <!-- Reason - select content -->
        <aha-select
          required
          class="my-2"
          v-if="modalType === 'reason'"
          v-model="reasonDeleteAccount"
          @change="reasonValidationError = ''"
        >
          <aha-select-option
            value=""
            disabled
            selected
            hidden
          >
            {{ $i18nPresenter.t('Select a reason…') }}
          </aha-select-option>
          <aha-select-option
            :value="reason"
            v-for="(reason, index) in modalContent.listContent"
            :key="index"
          >
            {{ reason }}
          </aha-select-option>
        </aha-select>

        <p class="mb-2 text-red-100" v-if="reasonValidationError">
          {{ reasonValidationError }}
        </p>

        <!-- More desc - all modals -->
        <p class="mb-2">
          {{ modalContent.moreDesc }}
        </p>

        <!-- Reason - input email -->
        <div v-if="modalType === 'reason'">
          <aha-input
            type="email"
            class="mb-2"
            :class="{'input-error': emailDeleteValidationError}"
            :placeholder="modalContent.placeHolder"
            v-model="emailDeleteAccount"
          />
          <p
            class="pb-2 text-red-100"
            v-if="emailDeleteValidationError"
          >
            {{ emailDeleteValidationError }}
          </p>
          <span class="text-red-100">{{ modalContent.warningText }}</span>
        </div>
        <!-- End of modal content -->

        <!-- Modal buttons -->
        <template #action>
          <aha-button
            variant="base"
            class="mr-2"
            v-if="modalContent.buttonLight"
            :disabled="isDeletingAccount && modalType === 'reason'"
            @click="handleClickButtonOnModal(modalType, 'light')"
          >
            {{ modalContent.buttonLight }}
          </aha-button>
          <aha-button
            v-if="modalContent.buttonPrimary"
            @click="handleClickButtonOnModal(modalType, 'primary')"
          >
            {{ modalContent.buttonPrimary }}
          </aha-button>
          <aha-button
            variant="danger"
            v-if="modalContent.buttonDanger"
            @click="handleClickButtonOnModal(modalType, 'danger')"
            :disabled="isDeletingAccount"
            icon-name="delete"
            :submitting="isDeletingAccount"
          >
            <aha-spinner v-if="isDeletingAccount" variant="white" class="mr-1" />
            {{ modalContent.buttonDanger }}
          </aha-button>
        </template>
        <!-- End of modal buttons -->
      </aha-modal>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'

import * as Sentry from '@sentry/vue'
import Utils from '@/util'
import constants from '@/constant'
import Socket from '@/socket'
import Config from '@/config'

// api
import UserApi from '@/api/user'
import StripeApi from '@/api/stripe'

// components
import PublicAnnouncement from '@/components-v2/common/global-notification/PublicAnnouncement.vue'
import InvitationNotifications from '@/components-v2/common/global-notification/InvitationNotifications.vue'
import EduYearlyNotification from '@/components-v2/common/global-notification/EduYearlyNotification.vue'
import PageBreadcrumb from '../common/page-breadcrumb/PageBreadcrumb.vue'
import ModalErrorMyProfileUpdateUser from '@/components-v2/modals/ErrorMyProfileUpdateUser.vue'
import TabEmailPreferences from '@/components-v2/my-profile/EmailPreferences.vue'
import TabLogOutEverywhereElse from '@/components-v2/my-profile/LogOutEverywhereElse.vue'
import OnboardingV3BackArrow from '../common/onboarding/OnboardingV3BackArrow.vue'
import UploadImageV2 from '@/components/presentations/UploadImage.v2.vue'
import CropImage from '@/components/presentations/CropImage'

import ActiveTabNames from '@/constant/ActiveTabNames'

const { eventCategories } = constants

export default {
  components: {
    PageBreadcrumb,
    InvitationNotifications,
    EduYearlyNotification,
    PublicAnnouncement,
    ModalErrorMyProfileUpdateUser,
    TabEmailPreferences,
    TabLogOutEverywhereElse,
    OnboardingV3BackArrow,
    UploadImageV2,
    CropImage,
  },
  data() {
    return {
      openProfileTab: 1,
      isUserUpdating: false,
      name: '',
      email: '',
      emailEdit: '',
      address: '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      textError: '',
      currentPassword3: '',
      hasErrorOnUpdatingUser: false,
      modalType: '',
      warningDeleteAccountVisible: false,
      reasonDeleteAccountVisible: false,
      confirmDeletedAccountVisible: false,
      emailDeleteAccount: '',
      reasonDeleteAccount: '',
      modalContent: {},
      emailDeleteValidationError: '',
      reasonValidationError: '',
      isDeletingAccount: false,
      showEditEmail: false,
      onboardingOptions: constants.onboardingOptions,
      languageOptions: constants.languageOptions,
      userPreferences: '',
      isCropImageModalShowing: false,
      avatarImageUrl: '',
      isUploadingAvatar: false,
      errorUploadImage: '',
      isUploadImageModalShowing: false,
      uploadAvatarTabs: [ActiveTabNames.UPLOAD],
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
      isShowChangeEmailForAhaSlides: (state) => {
        return state.currentUser.identifyProvider === null
      },
      isShowChangeEmailForGoogle: (state) => {
        return state.currentUser.identifyProvider === 'google' ||
          state.currentUser.identifyProvider === 'microsoft'
      },
      isShowChangePassword: (state) => {
        return state.currentUser.identifyProvider === null ||
          state.currentUser.identifyProvider === 'google' ||
          state.currentUser.identifyProvider === 'microsoft'
      },
      breadCumbText() {
        return this.$i18nPresenter.t('My Profile')
      },
      titleProfile() {
        return this.$i18nPresenter.t('Profile')
      },
      titleChangePassword() {
        return this.$i18nPresenter.t('Change password')
      },
      titleAccountDeletion() {
        return this.$i18nPresenter.t('Account deletion')
      },
      placeholderYourName() {
        return this.$i18nPresenter.t('Your name…')
      },
      placeholderEnterYourBillinInfo() {
        return this.$i18nPresenter.t('Enter your billing info... (for the receipts)')
      },
      placeholderEnterYourPassword() {
        return this.$i18nPresenter.t('Enter your password…')
      },
      placeholderTypeNewPassword() {
        return this.$i18nPresenter.t('Type in the new password…')
      },
      placeholderEnterNewPassword() {
        return this.$i18nPresenter.t('Enter new password…')
      },
      placeholderYourEmailAddress() {
        return this.$i18nPresenter.t('Your email address…')
      },
      placeholderEnterNewEmail() {
        return this.$i18nPresenter.t('Enter new email…')
      },
      titleDeleteYourAccount() {
        return this.$i18nPresenter.t('Delete your account')
      },
      iconLinked() {
        return this.currentUser.identifyProvider === 'google' ? 'gg' : 'microsoft'
      },
    }),
    modalTypeVisible() {
      if (this.modalType === 'warning') {
        return this.warningDeleteAccountVisible
      }
      if (this.modalType === 'reason') {
        return this.reasonDeleteAccountVisible
      }
      return this.confirmDeletedAccountVisible
    },
    isProviderGoogleOrMicrosoft() {
      return this.currentUser.identifyProvider === 'google' ||
        this.currentUser.identifyProvider === 'microsoft'
    },
  },
  watch: {
    currentUser() {
      this.resetValues()
    },
  },
  mounted() {
    this.resetValues()
  },
  methods: {
    ...mapActions([
      'logOutAccountRemoval',
      'setCurrentUserAction',
    ]),
    resetValues() {
      if (this.currentUser.id) {
        this.name = `${this.currentUser.firstName} ${this.currentUser.lastName}`
        this.email = this.currentUser.email
        this.address = this.currentUser.address
        this.emailingPromotions = this.currentUser.preferences.emailingPromotions
        this.emailingFeatureUpdates = this.currentUser.preferences.emailingFeatureUpdates
        this.emailingUsefulContents = this.currentUser.preferences.emailingUsefulContents
        this.userPreferences = this.currentUser.personalInformation ? this.currentUser.personalInformation.useAhaSlidesFor : ''
      }
    },

    onClickButtonChangeAvatar() {
      this.isUploadImageModalShowing = true
      this.trackUploadAvatar()
    },

    trackUploadAvatar() {
      Utils.mixpanelTracking(eventCategories.MY_ACCOUNT, 'Update profile > Avatar')
    },

    startUploadImage() {
      this.errorUploadImage = ''
      this.isUploadingAvatar = true
    },

    endUploadImage() {
      this.isUploadingAvatar = false
    },

    handleErrorUploadImage(error) {
      this.errorUploadImage = error
    },

    async handleUploadImage(imageUrl) {
      this.isUploadingAvatar = true
      this.isUploadImageModalShowing = false
      this.avatarImageUrl = imageUrl
      this.isCropImageModalShowing = true
    },

    handleCancelCrop() {
      this.isCropImageModalShowing = false
      this.isUploadingAvatar = false
    },
    async handleCropImageFinished(data) {
      this.isUploadingAvatar = true
      const { key } = data
      if (!await this.updateAvatar(key)) {
        const toastErrMsg = this.$i18nPresenter.t('The action cannot be performed!')
        Utils.showToastError(toastErrMsg)
      }
      setTimeout(() => {
        this.isUploadingAvatar = false
        this.isCropImageModalShowing = false
      }, 500)
    },

    async updateAvatar(path) {
      const res = await this.postUpdateAvatar(path)
      if (!res) {
        return false
      }
      const { abTest } = this.$store.state.currentUser
      await this.setCurrentUserAction({
        ...res.body,
        abTest,
      })
      return true
    },

    async postUpdateAvatar(path) {
      const payload = {
        avatar: path,
      }
      const userId = this.$store.state.currentUser.id
      try {
        return await UserApi.patchUserById(userId, payload)
      } catch (e) {
        Sentry.captureException(e)
        return false
      }
    },

    async updateProfile(index) {
      this.openProfileTab = index

      const payload = {
        role: this.currentUser.role,
      }

      this.textError = ''
      if (index === 1) {
        // Update billing address
        payload.address = this.address

        // Update user onboarding preferences
        payload.personalInformation = {
          useAhaSlidesFor: this.userPreferences,
        }
        // Update name
        this.name = this.name.trim()
        if (!this.name) {
          this.textError = this.$i18nPresenter.t('Please enter your name.')
          return
        }
        const arr = this.name.split(' ')
        if (arr.length === 1) {
          payload.firstName = this.name
          payload.lastName = ''
        } else {
          [payload.firstName] = arr
          payload.lastName = this.name.replace(`${arr[0]} `, '')
        }
        if (this.name.length > 40) {
          this.textError = this.$i18nPresenter.t('Your name must be no longer than 40 characters.')
          return
        }
      } else if (index === 2) {
        // Update password
        if (!this.currentPassword && this.currentUser.hasPassword) {
          this.textError = this.$i18nPresenter.t('Please enter your password.')
          return
        }

        if (this.newPassword.length < 8) {
          this.textError = this.$i18nPresenter.t('Password needs at least 8 characters.')
          return
        }

        if (this.newPassword.length > 20) {
          this.textError =
            this.$i18nPresenter.t('Your password must be no longer than 20 characters.')
          return
        }
        if (this.newPassword) {
          payload.password = this.currentPassword
          payload.newPassword = this.newPassword
        } else {
          this.textError = this.$i18nPresenter.t('Please enter your new password.')
          return
        }
      } else if (index === 3) {
        // Update email
        if (this.isProviderGoogleOrMicrosoft) {
          this.email = this.emailEdit
        }
        if (!this.currentUser.hasPassword) {
          if (this.email && this.newPassword) {
            this.email = this.email.trim()
            if (!this.isValidEmail()) return
            if (!this.isValidNewPassword()) return
            payload.email = this.email
            payload.newPassword = this.newPassword
          } else {
            this.textError = this.$i18nPresenter.t('Please enter your email or password.')
            return
          }
        } else if (this.email && this.currentPassword3) {
          this.email = this.email.trim()
          if (!this.isValidEmail()) return
          payload.email = this.email
          payload.password = this.currentPassword3
        } else {
          this.textError = this.$i18nPresenter.t('Please enter your email or password.')
          return
        }
      }

      if (this.isUserUpdating) return
      this.isUserUpdating = true
      this.hasErrorOnUpdatingUser = false

      try {
        const userId = this.$store.state.currentUser.id
        const res = await UserApi.patchUserById(userId, payload)
        if (this.currentUser.isPlayingDemoEnabled) {
          res.body.isPlayingDemoEnabled = true
        }
        if (index === 1) {
          Utils.mixpanelTracking(
            eventCategories.MY_ACCOUNT,
            'Update profile > Succeeded',
            `User ID: ${this.currentUser.id},
            Name: ${this.name},
            Use AhaSlides for: ${this.userPreferences},
            Billing address: ${this.address}`,
          )
          const { abTest } = this.$store.state.currentUser
          this.setCurrentUserAction({
            ...res.body,
            abTest,
          })
        }

        if (index === 2) {
          this.$store.state.currentUser.password = res.body.password
          this.$store.state.currentUser.salt = res.body.salt
          this.$store.state.currentUser.hasPassword = true
          if (payload.password) {
            Utils.mixpanelTracking(
              eventCategories.MY_ACCOUNT,
              'Profile > Change password > Succeeded',
            )
          } else {
            Utils.mixpanelTracking(
              eventCategories.MY_ACCOUNT,
              'Profile > New password > Succeeded',
            )
          }
        }

        if (index === 3) {
          const { currentUser } = this.$store.state
          Utils.mixpanelTracking(
            eventCategories.MY_ACCOUNT,
            'Recording email changes > Change email > Succeeded',
            `User ID: ${currentUser.id}, Old email: ${currentUser.email}, New email: ${this.email}`,
          )
          const { abTest } = this.$store.state.currentUser
          this.setCurrentUserAction({
            ...res.body,
            abTest,
          })
        }
      } catch (error) {
        Sentry.captureException(error)
        if (error.body.message) {
          this.textError = error.body.message
        } else {
          this.hasErrorOnUpdatingUser = true
        }

        Utils.mixpanelTracking(
          eventCategories.MY_ACCOUNT,
          'Update profile > Failed',
        )

        throw error
      } finally {
        this.isUserUpdating = false
      }
      const toastMsg = this.$i18nPresenter.t('Profile changed successfully!')
      Utils.showToastSuccess(toastMsg)

      this.currentPassword = ''
      this.newPassword = ''
      this.confirmPassword = ''
      this.textError = ''
      this.currentPassword3 = ''
      this.setFlagUserUpdatedGA4(this.$store.state.currentUser)
    },
    setFlagUserUpdatedGA4(user) {
      Utils.ga4Set('user_properties', {
        first_page: user.firstPage,
        referral_user_id: user.referralUserId,
        sign_up_source: user.source,
        use_ahaslides_for: user.personalInformation ? user.personalInformation.useAhaSlidesFor : null,
        updated_at: user.updatedAt,
      })
    },
    isValidEmail() {
      if (this.email === this.$store.state.currentUser.email) {
        this.textError = this.$i18nPresenter.t('This is your current email, please specify another email address.')
        return false
      }
      if (!Utils.validateEmail(this.email)) {
        this.textError = this.$i18nPresenter.t('Invalid email')
        return false
      }
      return true
    },
    isValidNewPassword() {
      if (this.newPassword.length < 8) {
        this.textError = this.$i18nPresenter.t('Password needs at least 8 characters.')
        return false
      }

      if (this.newPassword.length > 20) {
        this.textError =
          this.$i18nPresenter.t('Your password must be no longer than 20 characters.')
        return false
      }
      return true
    },
    handleClickEditEmail() {
      this.showEditEmail = !this.showEditEmail
    },

    showDeleteAccountModal(type) {
      this.modalType = type
      this.reasonValidationError = ''
      this.emailDeleteValidationError = ''

      if (type === 'warning') {
        this.modalContent = {
          desc: this.$i18nPresenter.t('Please note that if you delete your account now:'),
          listContent: [
            this.$i18nPresenter.t('currentPlanWillEndImmediately', {
              name: this.$store.state.plan.name,
            }),
            this.$i18nPresenter.t('Your presentations will be permanently removed and cannot be recovered.'),
            this.$i18nPresenter.t('All the response data you have collected will be lost.'),
          ],
          moreDesc: this.$i18nPresenter.t('If you don’t wish to delete you account entirely, you can consider changing your name, email address, or change your email preferences instead.'),
          buttonLight: this.$i18nPresenter.t('Continue deletion…'),
          buttonPrimary: this.$i18nPresenter.t('Go back'),
        }
        this.warningDeleteAccountVisible = true
        // this.refs['delete-account'].show()
        Utils.mixpanelTracking(constants.eventCategories.MY_ACCOUNT, 'Account removal > Delete account > Modal warning pops up')
      } else if (type === 'reason') {
        this.modalContent = {
          desc: this.$i18nPresenter.t('Please select a reason for account deletion:'),
          listContent: [
            this.$i18nPresenter.t('I have another AhaSlides account'),
            this.$i18nPresenter.t('AhaSlides does not provide what I’m looking for'),
            this.$i18nPresenter.t('I will no longer need AhaSlides'),
            this.$i18nPresenter.t('I created this account by accident'),
            this.$i18nPresenter.t('My organisation does not allow me to use AhaSlides'),
            this.$i18nPresenter.t('Other'),
          ],
          moreDesc: this.$i18nPresenter.t('Please confirm by typing your email address connected to this account:'),
          placeHolder: this.$i18nPresenter.t('Your email address…'),
          warningText: this.$i18nPresenter.t('Once you click “Delete account”, there’s no way to recover your account and data.'),
          buttonLight: this.$i18nPresenter.t('Go back'),
          buttonDanger: this.$i18nPresenter.t('Delete account'),
        }
        this.warningDeleteAccountVisible = false
        this.reasonDeleteAccountVisible = true
        this.reasonDeleteAccount = ''
        this.emailDeleteAccount = ''
        Utils.mixpanelTracking(constants.eventCategories.MY_ACCOUNT, 'Account removal > Continue deletion > Modal choose reason pops up')
      } else if (type === 'accountDeleted') {
        this.modalContent = {
          desc: this.$i18nPresenter.t('Your account is now deleted. We hope to see you again some day!'),
          buttonPrimary: 'OK',
        }
        this.reasonDeleteAccountVisible = false
        this.confirmDeletedAccountVisible = true
        Utils.mixpanelTracking(constants.eventCategories.MY_ACCOUNT, 'Account removal > Account deleted > Modal confirm account deleted pops up')
      }
    },
    closeDeleteAccountModal(type) {
      if (type === 'warning') {
        Utils.mixpanelTracking(constants.eventCategories.MY_ACCOUNT, 'Account removal > Button close modal warning > Modal warning closed')
        this.warningDeleteAccountVisible = false
      } else if (type === 'reason') {
        Utils.mixpanelTracking(constants.eventCategories.MY_ACCOUNT, 'Account removal > Button close modal reason > Modal reason closed')
        this.reasonDeleteAccountVisible = false
      } else if (type === 'accountDeleted') {
        Utils.mixpanelTracking(constants.eventCategories.MY_ACCOUNT, 'Account removal > Button close modal confirm account deleted > Sign out')
        this.closeModalAndSignOut()
      }
    },
    handleClickButtonOnModal(modalType, buttonType) {
      if (modalType === 'warning') {
        this.warningDeleteAccountVisible = false
        if (buttonType === 'light') {
          setTimeout(() => {
            this.showDeleteAccountModal('reason')
          }, 300)
        } else {
          Utils.mixpanelTracking(constants.eventCategories.MY_ACCOUNT, 'Account removal > Go back > Modal warning closed')
        }
      } else if (modalType === 'reason') {
        if (buttonType === 'danger') {
          Utils.mixpanelTracking(constants.eventCategories.MY_ACCOUNT, 'Account removal > Confirm delete account > Validate reason and email for account removal')
          if (!this.validateFormDeleteAccount()) return
          this.isDeletingAccount = true
          this.handleDeleteAccount()
        } else if (buttonType === 'light') {
          this.reasonDeleteAccountVisible = false
          Utils.mixpanelTracking(constants.eventCategories.MY_ACCOUNT, 'Account removal > Go back > Modal reason closed')
        }
      } else if (modalType === 'accountDeleted') {
        Utils.mixpanelTracking(constants.eventCategories.MY_ACCOUNT, 'Account removal > Confirm account deleted > Sign out')
        this.closeModalAndSignOut()
      }
    },
    validateFormDeleteAccount() {
      let check = false
      this.emailDeleteValidationError = ''
      if (this.reasonDeleteAccount.length && this.validateEmailDeleteAccount()) {
        check = true
      }
      if (!this.reasonDeleteAccount.length &&
        (this.validateEmailDeleteAccount() || !this.validateEmailDeleteAccount())
      ) {
        this.reasonValidationError = this.$i18nPresenter.t('Please choose a reason.')
        check = false
      }
      return check
    },
    validateEmailDeleteAccount() {
      if (!this.emailDeleteAccount.trim()) {
        this.emailDeleteValidationError = this.$i18nPresenter.t('Please enter your email.')
        return false
      }
      if (this.emailDeleteAccount.trim() !== this.email) {
        this.emailDeleteValidationError = this.$i18nPresenter.t('Email address does not match.')
        return false
      }
      return true
    },

    hasStripeSubscription(user) {
      return user.stripeCustomerId !== null
    },

    async handleDeleteAccount() {
      const payload = {
        reasonDeleteAccount: this.reasonDeleteAccount,
        email: this.emailDeleteAccount.trim(),
        active: false,
        socketId: Socket.getSocketId(),
      }
      const payloadSubscription = {
        stop: true,
        reason: 'Account removal',
      }

      try {
        if (this.$store.state.plan.name !== 'Free' &&
          this.$store.state.plan.group !== 'one-time' &&
          this.hasStripeSubscription(this.$store.state.currentUser)
        ) {
          await StripeApi.cancelSubscription(payloadSubscription)
        }
        await this.$http.delete(Config.api.removeUserAccount, { body: payload })
        this.reasonDeleteAccountVisible = false
        setTimeout(() => {
          this.showDeleteAccountModal('accountDeleted')
        }, 300)
      } catch (error) {
        Sentry.captureException(error)
        const toastMsg = this.$i18nPresenter.t('Delete account failed')
        Utils.showToastError(toastMsg)
      } finally {
        this.isDeletingAccount = false
      }
    },
    closeModalAndSignOut() {
      this.confirmDeletedAccountVisible = false
      this.logOutAccountRemoval()
    },
  },
}
</script>

<style lang="scss" scoped>
.table-plan {
  @apply w-full;
}
.table-plan-property {
  @apply font-bold py-2 w-1/4 align-top;
}

.table-plan-value {
  @apply py-2 align-top;
  word-break: break-word;
}
@media (max-width: 480px) {
  .profile-tab {
    &::v-deep .aha-tab-list-wrp {
      white-space: nowrap;
    }
  }
}

.container-user-profile{
  .contain-avatar{
    .avatar-group {
      margin-top: 5px;
      margin-bottom: 15px;
      .avatar-image {
        &:hover{
          cursor: pointer;
        }

        .aha-avatar{
          width: inherit;
          height: inherit;
          font-size: 44px;
          font-weight: bold;
          img {
            border-radius: 50%;
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
        }

        .loading-show {
          display: flex;
          justify-content: center;
          align-items: center;
          position: absolute;
          border-radius: 50%;
          left: 0;
          top: 0;
          width: 100%;
          height: 100%;
          background-color: #f3f3f3ba;
        }

        position: relative;
        width: 108px;
        height: 108px;
        margin: 0 auto;
      }
    }
    margin: 0 auto;
    text-align: center;
    .aha-button{
      align-items: end;
    }
  }
}

@media (min-width: 768px) {
  .container-user-profile {
    .contain-avatar {
      .avatar-image {
      }
    }
  }
}

@media (min-width: 1024px) {
  .table-change-email{
    .table-plan-property{
      width: 30%;
    }
    .table-plan-value{
      padding-left: 20px;
      padding-right: 20px;
    }
  }

  .container-user-profile{
    display: flex;
    .contain-avatar{
      .avatar-group{
      }
      text-align: center;
      width: 30%;
    }
    .contain-info{
      width: 70%;
      padding-left: 20px;
      padding-right: 20px;
    }
  }
}

</style>
